/**
 * Roraima's tepui, in the shared 120×120 LINE asset box.
 *
 * TRACED from the theme's own icon, not drawn by hand — the same method the
 * Autumn maple used, and for the same reason: a tepui reads by ONE thing, the
 * dead-level rim that steps down across its length, and eyeballing a table
 * mountain turns it into a loaf. The icon's silhouette was masked (dark AND
 * warm, so the violet top of the sunset sky is not mistaken for rock), the
 * skyline traced column by column, Douglas-Peucker simplified to 18 points and
 * normalised into the 120 box.
 *
 * The trace also recovered the icon's signature detail: a DETACHED PILLAR
 * standing off the left end of the wall, with a clear gap between it and the
 * cliff. That prow is what makes the shape Roraima rather than any mesa, so it
 * is kept as its own path and drawn beside the body everywhere.
 *
 * Straight segments, not curves. Every other glyph in this family is smoothed
 * through segment midpoints, but a tepui's rim is a hard edge — softening it is
 * exactly what would lose the shape.
 *
 * It is drawn in four places — the passcode dots, the Home tab button, the menu
 * strip and the chat wallpaper — so the geometry lives here and all four import
 * it, the rule the Everest summit, the Aurora orb, the Sequoia tree and the
 * Autumn maple already follow ([[line-shared-glyph-modules]]).
 */

/** The wall: the traced rim, then the right cliff, the base and the left cliff.
 *  Cliffs flare outward toward the base, the way the real talus does. */
export const TEPUI_BODY = 'M23.0,35.7 L25.3,36.1 L31.1,33.4 L35.7,34.6 L36.1,33.0 L40.3,35.0'
  + ' L44.2,33.8 L57.6,35.7 L62.6,39.3 L65.2,38.1 L72.5,41.6 L75.8,44.4 L88.4,48.3'
  + ' L93.7,47.9 L96.3,45.9 L105.9,47.5 L108.5,46.3 L114.0,53.0'
  + ' L117.0,104.0 L20.0,104.0 Z';

/** The detached pillar off the left end — the icon's prow. */
export const TEPUI_SPIRE = 'M9.0,47.5 L16.2,45.5 L17.4,104.0 L8.2,104.0 Z';

/** The sunlit crown: the same rim, offset down, so the top of the wall catches
 *  the last light and the face below it stays in shadow. */
export const TEPUI_CROWN = 'M23.0,35.7 L25.3,36.1 L31.1,33.4 L35.7,34.6 L36.1,33.0 L40.3,35.0'
  + ' L44.2,33.8 L57.6,35.7 L62.6,39.3 L65.2,38.1 L72.5,41.6 L75.8,44.4 L88.4,48.3'
  + ' L93.7,47.9 L96.3,45.9 L105.9,47.5 L108.5,46.3 L114.0,53.0'
  + ' L114.4,58.6 L108.6,51.9 L106.0,53.1 L96.4,51.5 L93.8,53.5 L88.3,53.9'
  + ' L75.4,50.0 L72.1,47.2 L64.9,43.7 L62.3,44.9 L57.3,41.3 L44.2,39.4 L40.2,40.6'
  + ' L36.0,38.6 L35.6,40.2 L31.0,39.0 L25.2,41.7 L22.6,41.3 Z';

/**
 * The water stains that run down a tepui's face — the black streaks in every
 * photograph of the real thing, left by the waterfalls that pour off the rim.
 *
 * VERTICAL on purpose. The first pass drew horizontal bedding planes instead,
 * and three parallel lines across a rectangle read as the slats of a crate, not
 * as rock. Runnels also say Roraima specifically, where bedding says "cliff".
 *
 * IRREGULAR on purpose too, in both spacing and length. Evenly spaced full-height
 * strokes were the second version's mistake and they read as a barcode: real
 * stains start wherever the rim happens to shed water and dry out at different
 * heights. Each starts just under the rim it falls from and stops short of the
 * base.
 */
export const TEPUI_RUNNELS = 'M29,44 L29,88 M46,43 L46,99 M58,48 L58,74'
  + ' M77,53 L77,96 M99,56 L99,81';

/** The sun the icon puts behind the wall's right shoulder: only its upper arc
 *  clears the rim, which is what makes the rim read as a horizon. */
export const TEPUI_SUN = { cx: 89, cy: 43, r: 11.5 };

/**
 * One bank of the cloud sea: a unit shape on the origin, about 22 wide and 8
 * tall, with lobes on top and a FLAT base — cloud seen edge-on, which is what a
 * tepui stands in.
 *
 * The first pass made this a plain lens and it came out as a grain of rice at
 * every size: a cloud needs bumps to be a cloud, and a flat underside is what
 * separates a bank of it from a puff floating in the air. Kept flat-shaded and
 * unblurred like every other kawaii shape in this family — and because a blur
 * that reaches the menu strip's edge would fade there and bake a seam.
 */
export const TEPUI_WISP = 'M-11,1.7 Q-13,1.7 -13,0 Q-13,-1.7 -10.6,-2.1'
  + ' Q-9.6,-4.6 -6.4,-4.4 Q-4.6,-6.2 -1.8,-5.6 Q0.2,-7.1 2.8,-5.9'
  + ' Q5.8,-6.4 6.9,-4.0 Q9.8,-3.8 10.6,-1.8 Q13,-1.6 13,0 Q13,1.7 11,1.7 Z';

/** Roraima's rock, base to rim — its own palette, deepest first. */
export const TEPUI_DEEP = '#3E2622';    // the base, still in night
export const TEPUI_MID = '#7A4436';     // the face
export const TEPUI_LIGHT = '#CC654E';   // Sunset Mesa: the rim in the last light
/** The lit crown slab. */
export const TEPUI_RIM = '#D69568';     // Amber Sand
/** The crown on a glyph whose body is already the flat ON colour: one step
 *  lighter, so the rim still reads as the lit edge instead of merging. Tracks
 *  the ON tab colour, so it moved to a pale pink when ON became Pink Quartz. */
export const TEPUI_SUNLIT = '#F8C4C4';
/** The sun behind the wall, and the warmth around it. */
export const TEPUI_SUN_C = '#DDBE6D';   // Dry Grass gold
export const TEPUI_GLOW = '#D17458';    // Coral Canyon
/** Bedding-plane ink. */
export const TEPUI_SEAM = '#2E1D1B';
/** ONE outline tone for every unentered wall — the frame never changes per
 *  digit (the rule Aurora's orbs settled). */
export const TEPUI_OFF = '#A98D85';
